import React, { useEffect, useState, useMemo } from "react";
import { Button, Form, Dropdown } from "react-bootstrap";
import { startOfMonth, endOfMonth, format } from "date-fns";
import useFetchDatav2 from "./useFetchDatav2";
import TableSettingsDropdown from "./datatable/TableSettingsDropdown";
import ReportSummaryStrip from "./ReportSummaryStrip";
import ReportDateRange from "./ReportDateRange";
import SelectFilter from "./SelectFilter";
import { reports } from "../reports";
import { rowsToCsv, downloadCsv } from "../utils/reportCsv";
import { valueLabel, formatDuration } from "../reports/format";
import { usePreferences, useMoneyFormat } from "../Context/PreferencesContext";
import { shouldAccentNegative } from "../utils/formatters";


// ── Formatters ────────────────────────────────────────────────────────────────
// `money` and `cell` are defined inside the component (they read the user's money
// prefs via hooks). Report table cells omit the $ symbol (summary strip shows it).

// Period label: a whole single calendar month → "MMM yyyy"; otherwise "MMM d – MMM d, yyyy".
const periodLabel = (fromIso, toIso) => {
   const s = new Date(fromIso);
   const e = new Date(toIso);
   const whole =
      format(s, "yyyy-MM-dd") === format(startOfMonth(s), "yyyy-MM-dd") &&
      format(e, "yyyy-MM-dd") === format(endOfMonth(s), "yyyy-MM-dd");
   return whole ? format(s, "MMM yyyy") : `${format(s, "MMM d")} – ${format(e, "MMM d, yyyy")}`;
};

// ── Sort comparator ───────────────────────────────────────────────────────────
// Nulls go last; handles numbers, ISO date strings, other strings.
function compareBy(key, dir) {
   return (a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      let cmp;
      if (typeof av === "number" && typeof bv === "number") {
         cmp = av - bv;
      } else {
         cmp = String(av).localeCompare(String(bv));
      }
      return dir === "desc" ? -cmp : cmp;
   };
}

// ── Group rows by a field, computing subtotals for sum-total columns ──────────
function groupRows(rows, field, sortKey, sortDir, columns) {
   const sumCols = (columns || []).filter((c) => c.total === "sum").map((c) => c.key);
   const map = new Map();
   for (const row of rows) {
      const k = row[field] ?? "(blank)";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(row);
   }
   return Array.from(map.entries()).map(([key, groupRows_]) => {
      const sorted =
         sortKey
            ? [...groupRows_].sort(compareBy(sortKey, sortDir))
            : groupRows_;
      const subtotals = {};
      for (const col of sumCols) {
         subtotals[col] = sorted.reduce((s, r) => s + (r[col] ?? 0), 0);
      }
      return { key, label: key, rows: sorted, subtotals };
   });
}

// ── Compute summary from flat row list, driven by def.summary agg specs ───────
function computeSummary(rows, summaryDefs) {
   return summaryDefs.map((spec) => {
      let value;
      switch (spec.agg) {
         case "count":
            value = rows.length;
            break;
         case "sum":
            value = rows.reduce((s, r) => s + (r[spec.column] ?? 0), 0);
            break;
         case "countWhere":
            value = rows.filter((r) => r[spec.column] === spec.equals).length;
            break;
         case "sumExposure":
            value = rows.reduce(
               (s, r) => s + Math.abs(r.variance ?? r.statement_amount ?? r.erp_amount ?? 0),
               0
            );
            break;
         default:
            value = 0;
      }
      return { key: spec.key, label: spec.label, type: spec.type, value };
   });
}

// ── Compute totals (sum columns) from flat row list ───────────────────────────
function computeTotals(rows, columns) {
   const totals = {};
   for (const col of columns) {
      if (col.total === "sum") {
         totals[col.key] = rows.reduce((s, r) => s + (r[col.key] ?? 0), 0);
      }
   }
   return totals;
}

// ── Multiselect Dropdown ──────────────────────────────────────────────────────
// A small react-bootstrap Dropdown with Form.Check checkboxes.
// selected = Set of selected values; all selected = show everything.
function MultiSelectDropdown({ label, options, selected, onChange, labelFn }) {
   // selected === null means "all selected" (no filter active)
   const allSelected = selected === null || selected.size === options.length;

   const toggle = (val) => {
      const next = new Set(selected ?? options);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      // If all are selected, collapse back to null (no filter)
      onChange(next.size === options.length ? null : next);
   };

   const toggleAll = () => {
      if (allSelected) {
         // Deselect all — set to empty set (nothing passes filter = zero rows shown)
         onChange(new Set());
      } else {
         // Select all — clear filter
         onChange(null);
      }
   };

   const displayLabel = !allSelected && selected
      ? `${selected.size}/${options.length}`
      : label;

   return (
      <Dropdown>
         <Dropdown.Toggle
            variant="outline-secondary"
            id={`filter-${label}`}
         >
            {displayLabel}
         </Dropdown.Toggle>
         <Dropdown.Menu
            onClick={(e) => e.stopPropagation()}
            style={{ minWidth: 180, maxHeight: 260, overflowY: "auto" }}
         >
            <div className="px-3 py-1">
               <Form.Check
                  type="checkbox"
                  id={`ms-${label}-all`}
                  label="All"
                  checked={allSelected}
                  onChange={toggleAll}
                  style={{ fontSize: 13 }}
               />
            </div>
            <Dropdown.Divider />
            {options.map((opt) => (
               <div key={opt} className="px-3 py-1">
                  <Form.Check
                     type="checkbox"
                     id={`ms-${label}-${opt}`}
                     label={labelFn ? labelFn(opt) : opt}
                     checked={allSelected || (selected != null && selected.has(opt))}
                     onChange={() => toggle(opt)}
                     style={{ fontSize: 13 }}
                  />
               </div>
            ))}
         </Dropdown.Menu>
      </Dropdown>
   );
}

// ── Sortable header cell ──────────────────────────────────────────────────────
const SORT_CARETS = { asc: " ▲", desc: " ▼", none: "" };

// ═════════════════════════════════════════════════════════════════════════════
export default function ReportRunner({ reportId }) {
   const def = reports[reportId];
   const { fetchData } = useFetchDatav2();
   const { prefs } = usePreferences();
   const fmtMoney = useMoneyFormat();

   // Report tables omit the $ symbol (the summary strip shows it); honor the user's
   // decimals/grouping/negative-style/red via the shared formatter.
   const money = (v) => (v == null ? "" : fmtMoney(v, { symbol: false }));
   const cell = (col, row) => {
      const v = row[col.key];
      if (col.type === "money") {
         if (v == null) return <span style={{ color: "#6c757d" }}>—</span>;
         const accent = col.accent && shouldAccentNegative(Number(v), prefs.money);
         return (
            <span style={{ fontVariantNumeric: "tabular-nums", color: accent ? "#dc3545" : undefined }}>
               {money(Number(v))}
            </span>
         );
      }
      if (col.type === "label") return valueLabel(col.key, v);
      if (col.type === "duration") return formatDuration(v);
      if (col.type === "percent") return v == null || v === "" ? "" : `${v}%`;
      return v ?? "";
   };

   const [from, setFrom] = useState(null);
   const [to, setTo] = useState(null);
   const [minVariance, setMinVariance] = useState("");
   // Params used for the currently-displayed envelope (drives the "changed — re-run" hint).
   const [lastRun, setLastRun] = useState(null); // { from, to, minVariance } | null
   const [envelope, setEnvelope] = useState(null);
   const [loading, setLoading] = useState(false);

   // ── Filter state ────────────────────────────────────────────────────────
   // null = all selected (no filter); Set = restrict to these values
   const [filterLocations, setFilterLocations] = useState(null);
   const [filterSuppliers, setFilterSuppliers] = useState(null);
   const [filterCategories, setFilterCategories] = useState(null);

   // ── Group-by state ──────────────────────────────────────────────────────
   const [groupByField, setGroupByField] = useState(def.groupBy[0] ?? "supplier");

   // ── Sort state ──────────────────────────────────────────────────────────
   const [sortKey, setSortKey] = useState(null);
   const [sortDir, setSortDir] = useState("asc");

   // ── Column visibility + order ───────────────────────────────────────────
   const [visibleKeys, setVisibleKeys] = useState(() => def.columns.map((c) => c.key));

   // ── Pagination state ────────────────────────────────────────────────────
   const [pageIndex, setPageIndex] = useState(0);
   const [pageSize, setPageSize] = useState(50);

   // ── Settings cog (shared TableSettingsDropdown — temp state + Apply/Cancel) ─
   // Committed state stays visibleKeys + pageSize; the menu edits a temp copy that
   // is only flushed on Apply, matching the Documents/Suppliers gear behaviour.
   const [showGear, setShowGear] = useState(false);
   const [tempPageSize, setTempPageSize] = useState(pageSize);
   const [tempVis, setTempVis] = useState({}); // { [colKey]: bool }

   const seedTemp = () => {
      setTempPageSize(pageSize);
      setTempVis(Object.fromEntries(def.columns.map((c) => [c.key, visibleKeys.includes(c.key)])));
   };
   const handleToggleDropdown = (open) => { if (open) seedTemp(); setShowGear(open); };
   const handlePageSizeChange = (n) => setTempPageSize(n);
   const handleColumnVisibilityChange = (field) =>
      setTempVis((v) => ({ ...v, [field]: !(v[field] ?? true) }));
   const handleApplyChanges = () => {
      // Rebuild visibleKeys in definition order (reorder was dropped with the old gear).
      const next = def.columns.map((c) => c.key).filter((k) => tempVis[k]);
      setVisibleKeys(next.length ? next : [def.columns[0].key]); // keep ≥1 column
      setPageSize(tempPageSize);
      setPageIndex(0);
      setShowGear(false);
   };
   const handleCancelChanges = () => setShowGear(false);

   const runLoad = (fromArg, toArg, minVar) => {
      if (!fromArg || !toArg) return; // Run is gated on a chosen range
      setLoading(true);
      // Same-origin relative path (like every other .sz call).
      const url = `${def.endpoint}?from=${fromArg}&to=${toArg}&minVariance=${minVar}`;
      fetchData(url).then(({ responseData }) => {
         // Endpoint streams a FLAT array (payload). Grouping/summary/totals computed below.
         const rows = Array.isArray(responseData?.payload) ? responseData.payload : [];
         setEnvelope({ rows, period: { label: periodLabel(fromArg, toArg) } });
         setLastRun({ from: fromArg, to: toArg, minVariance: minVar });
         setLoading(false);
      });
   };

   // ── All rows from the flat payload ─────────────────────────────────────
   const allRows = useMemo(() => envelope?.rows ?? [], [envelope]);

   // ── Distinct filter options (memoized) ──────────────────────────────────
   const optLocations = useMemo(
      () => [...new Set(allRows.map((r) => r.location).filter(Boolean))].sort(),
      [allRows]
   );
   const optSuppliers = useMemo(
      () => [...new Set(allRows.map((r) => r.supplier).filter(Boolean))].sort(),
      [allRows]
   );
   const optCategories = useMemo(
      () => [...new Set(allRows.map((r) => r[def.categoryField]).filter(Boolean))].sort(),
      [allRows, def.categoryField]
   );

   // ── Apply filters ────────────────────────────────────────────────────────
   const filteredRows = useMemo(() => {
      return allRows.filter((row) => {
         if (filterLocations  != null && !filterLocations.has(row.location)) return false;
         if (filterSuppliers  != null && !filterSuppliers.has(row.supplier)) return false;
         if (filterCategories != null && !filterCategories.has(row[def.categoryField])) return false;
         return true;
      });
   }, [allRows, filterLocations, filterSuppliers, filterCategories, def.categoryField]);

   // ── Derived summary + totals from filtered rows ──────────────────────────
   const derivedSummary = useMemo(() => computeSummary(filteredRows, def.summary), [filteredRows, def.summary]);
   const visibleColObjects = useMemo(
      () => visibleKeys.map((k) => def.columns.find((c) => c.key === k)).filter(Boolean),
      [visibleKeys, def.columns]
   );
   const derivedTotals = useMemo(
      () => computeTotals(filteredRows, visibleColObjects),
      [filteredRows, visibleColObjects]
   );

   // ── Sorted flat list (for pagination slicing) ─────────────────────────────
   const sortedRows = useMemo(() => {
      if (!sortKey) return filteredRows;
      return [...filteredRows].sort(compareBy(sortKey, sortDir));
   }, [filteredRows, sortKey, sortDir]);

   // ── Pagination derived values ─────────────────────────────────────────────
   const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
   const clampedPage = Math.min(pageIndex, totalPages - 1);

   // ── Reset pageIndex when filters/sort/groupBy/pageSize change ────────────
   useEffect(() => { setPageIndex(0); }, [filterLocations, filterSuppliers, filterCategories, groupByField, sortKey, sortDir, pageSize]);

   // ── Page-sliced rows → grouped for display ────────────────────────────────
   const pageRows = useMemo(
      () => sortedRows.slice(clampedPage * pageSize, clampedPage * pageSize + pageSize),
      [sortedRows, clampedPage, pageSize]
   );

   // ── Group + sort (page slice only — for table body) ───────────────────────
   const derivedGroups = useMemo(
      () => groupRows(pageRows, groupByField, null /* already sorted */, sortDir, def.columns),
      [pageRows, groupByField, sortDir, def.columns]
   );

   // ── Sort toggle ──────────────────────────────────────────────────────────
   const handleSort = (key) => {
      if (sortKey === key) {
         if (sortDir === "asc") setSortDir("desc");
         else if (sortDir === "desc") { setSortKey(null); setSortDir("asc"); }
      } else {
         setSortKey(key);
         setSortDir("asc");
      }
   };

   // ── Styles ────────────────────────────────────────────────────────────────
   // Base th/td typography + hover live in scss/pages/_reports.scss (.reports-page).
   const colSpanCount = visibleColObjects.length;

   const firstSumCol = visibleColObjects.find((c) => c.total === "sum");

   return (
      <div>
         {/* ── Title + Export ─────────────────────────────────────────────── */}
         <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 style={{ fontWeight: 600, margin: 0 }}>{def.title}</h5>
            <Button
               variant="outline-secondary"
               size="sm"
               disabled={!envelope}
               onClick={() =>
                  downloadCsv(
                     `${def.id}-${envelope.period.label}.csv`,
                     rowsToCsv(filteredRows, visibleColObjects)
                  )
               }
            >
               Export CSV
            </Button>
         </div>

         {/* ── Card shell (matches Documents / Suppliers .data-table-card) ── */}
         <div className="data-table-card">
         {/* ── Param / filter bar ────────────────────────────────────────── */}
         <div
            className="d-flex gap-3 align-items-end flex-wrap py-2 border-bottom"
            style={{ position: "sticky", top: 0, background: "var(--rk-surface)", zIndex: 1 }}
         >
            <Form.Group>
               <Form.Label style={{ fontSize: 13, color: "#6c757d", marginBottom: 2 }}>
                  Period
               </Form.Label>
               <ReportDateRange
                  value={{ from, to }}
                  onChange={({ from: f, to: t }) => { setFrom(f); setTo(t); }}
               />
            </Form.Group>

            {optLocations.length > 0 && (
               <Form.Group>
                  <Form.Label style={{ fontSize: 13, color: "#6c757d", marginBottom: 2 }}>
                     Location
                  </Form.Label>
                  <div>
                     <MultiSelectDropdown
                        label="Location"
                        options={optLocations}
                        selected={filterLocations}
                        onChange={setFilterLocations}
                     />
                  </div>
               </Form.Group>
            )}

            {optSuppliers.length > 0 && (
               <Form.Group>
                  <Form.Label style={{ fontSize: 13, color: "#6c757d", marginBottom: 2 }}>
                     Supplier
                  </Form.Label>
                  <div>
                     <MultiSelectDropdown
                        label="Supplier"
                        options={optSuppliers}
                        selected={filterSuppliers}
                        onChange={setFilterSuppliers}
                     />
                  </div>
               </Form.Group>
            )}

            {optCategories.length > 0 && (
               <Form.Group>
                  <Form.Label style={{ fontSize: 13, color: "#6c757d", marginBottom: 2 }}>
                     {def.categoryLabel}
                  </Form.Label>
                  <div>
                     <MultiSelectDropdown
                        label={def.categoryLabel}
                        options={optCategories}
                        selected={filterCategories}
                        onChange={setFilterCategories}
                        labelFn={(v) => valueLabel(def.categoryField, v)}
                     />
                  </div>
               </Form.Group>
            )}

            {def.minLabel && (
            <Form.Group>
               <Form.Label style={{ fontSize: 13, color: "#6c757d", marginBottom: 2 }}>
                  {def.minLabel}
               </Form.Label>
               <Form.Control
                  type="number"
                  value={minVariance}
                  style={{ width: 120 }}
                  onChange={(e) => setMinVariance(e.target.value)}
               />
            </Form.Group>
            )}

            <Form.Group>
               <Form.Label style={{ fontSize: 13, color: "#6c757d", marginBottom: 2, display: "block" }}>
                  &nbsp;
               </Form.Label>
               <Button
                  variant="primary"
                  data-testid="report-run"
                  disabled={!from || !to || loading}
                  onClick={() => runLoad(from, to, minVariance)}
               >
                  Run
               </Button>
            </Form.Group>

            {envelope && lastRun &&
               (from !== lastRun.from || to !== lastRun.to || String(minVariance) !== String(lastRun.minVariance)) && (
               <div style={{ fontSize: 12, color: "#6c757d", alignSelf: "center" }}>
                  Parameters changed — click Run to refresh.
               </div>
            )}

            {envelope && (
            <Form.Group>
               <Form.Label style={{ fontSize: 13, color: "#6c757d", marginBottom: 2 }}>
                  Group by
               </Form.Label>
               <div style={{ width: 160 }}>
                  <SelectFilter
                     column={{ id: "report-groupby", value: groupByField }}
                     constants={def.groupOptions ?? []}
                     handleInputFilterChangeNoDebounce={(_id, val) => setGroupByField(val)}
                     placeholder={null}
                     className="form-select"
                  />
               </div>
            </Form.Group>
            )}
         </div>

         {/* ── Summary strip (derived from filtered rows) ─────────────────── */}
         {envelope && <ReportSummaryStrip summary={derivedSummary} />}

         {/* ── Table (settings cog lives in the last header cell) ────────── */}
         <table className="table table-sm align-middle" style={{ marginTop: 12 }}>
            <thead>
               <tr>
                  {visibleColObjects.map((c, ci) => {
                     const active = sortKey === c.key;
                     const caret = active ? SORT_CARETS[sortDir] : "";
                     const isLast = ci === visibleColObjects.length - 1;
                     const label = (
                        <span
                           style={{ cursor: "pointer", userSelect: "none" }}
                           onClick={() => handleSort(c.key)}
                        >
                           {c.label}
                           {caret && (
                              <span style={{ fontSize: 10, color: "#6c757d" }}>{caret}</span>
                           )}
                        </span>
                     );
                     return (
                        <th key={c.key} style={{ textAlign: c.align || "left" }}>
                           {isLast ? (
                              <span className="d-inline-flex align-items-center gap-4">
                                 {label}
                                 <TableSettingsDropdown
                                    showDropdown={showGear}
                                    handleToggleDropdown={handleToggleDropdown}
                                    tempPageSize={tempPageSize}
                                    handlePageSizeChange={handlePageSizeChange}
                                    columns={def.columns.map((col) => ({ field: col.key, label: col.label }))}
                                    tempColumnVisibility={tempVis}
                                    handleColumnVisibilityChange={handleColumnVisibilityChange}
                                    handleApplyChanges={handleApplyChanges}
                                    handleCancelChanges={handleCancelChanges}
                                 />
                              </span>
                           ) : label}
                        </th>
                     );
                  })}
               </tr>
            </thead>
            <tbody>
               {!loading && !envelope && (
                  <tr>
                     <td
                        colSpan={colSpanCount}
                        style={{ textAlign: "center", color: "#6c757d", padding: "32px 0" }}
                     >
                        Choose a date range and click Run to load the report.
                     </td>
                  </tr>
               )}
               {loading &&
                  Array.from({ length: 8 }, (_, i) => (
                     <tr key={`skel-${i}`} aria-hidden="true">
                        {visibleColObjects.map((c, j) => (
                           <td key={c.key} style={{ textAlign: c.align || "left" }}>
                              <span className="dt-skel" style={{ width: ["72%","48%","62%","55%","68%"][(i + j) % 5], maxWidth: 180 }} />
                           </td>
                        ))}
                     </tr>
                  ))}
               {!loading && envelope && allRows.length === 0 && (
                  <tr>
                     <td colSpan={colSpanCount}>
                        No data for the selected period.
                     </td>
                  </tr>
               )}
               {!loading && envelope && allRows.length > 0 && filteredRows.length === 0 && (
                  <tr>
                     <td colSpan={colSpanCount}>
                        No rows match these filters.
                     </td>
                  </tr>
               )}
               {!loading &&
                  derivedGroups.map((g) => (
                     <React.Fragment key={g.key}>
                        <tr style={{ borderTop: "1px solid #dee2e6" }}>
                           <td
                              colSpan={colSpanCount}
                              style={{ fontWeight: 600 }}
                              data-group-header={g.key}
                           >
                              {valueLabel(groupByField, g.key)}{" "}
                              <span style={{ color: "#6c757d", fontWeight: 400 }}>
                                 · {g.rows.length} items
                                 {firstSumCol ? ` · ${money(g.subtotals[firstSumCol.key])}` : ""}
                              </span>
                           </td>
                        </tr>
                        {g.rows.map((row, i) => (
                           <tr key={i}>
                              {visibleColObjects.map((c) => (
                                 <td
                                    key={c.key}
                                    style={{ textAlign: c.align || "left" }}
                                 >
                                    {cell(c, row)}
                                 </td>
                              ))}
                           </tr>
                        ))}
                     </React.Fragment>
                  ))}
            </tbody>
            {envelope && filteredRows.length > 0 && (
               <tfoot>
                  <tr style={{ borderTop: "2px solid #000", fontWeight: 600 }}>
                     {visibleColObjects.map((c) => (
                        <td
                           key={c.key}
                           style={{
                              fontWeight: 600,
                              textAlign: c.align || "left",
                              fontVariantNumeric: "tabular-nums",
                           }}
                        >
                           {c.key === firstSumCol?.key
                              ? "Grand total"
                              : c.total === "sum"
                              ? money(derivedTotals[c.key])
                              : ""}
                        </td>
                     ))}
                  </tr>
               </tfoot>
            )}
         </table>

         {/* ── Pager footer ──────────────────────────────────────────────── */}
         {envelope && filteredRows.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
               <span style={{ fontSize: 13, color: "#6c757d" }}>
                  {filteredRows.length}{" "}
                  {def.id === "discrepancies"
                     ? (filteredRows.length === 1 ? "discrepancy" : "discrepancies")
                     : (filteredRows.length === 1 ? "row" : "rows")}
               </span>
               <div className="d-flex justify-content-center flex-grow-1">
                  <span style={{ fontSize: 13 }}>
                     Page <strong>{clampedPage + 1}</strong> of {totalPages}
                  </span>
               </div>
               <div className="d-flex gap-2">
                  <button
                     className="btn btn-primary btn-sm me-1"
                     onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                     disabled={clampedPage === 0}
                  >
                     Previous
                  </button>
                  <button
                     className="btn btn-primary btn-sm"
                     onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
                     disabled={clampedPage >= totalPages - 1}
                  >
                     Next
                  </button>
               </div>
            </div>
         )}
         </div>{/* /.data-table-card */}
      </div>
   );
}
